import AddIcon from '@mui/icons-material/Add';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EventIcon from '@mui/icons-material/Event';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import ExtensionIcon from '@mui/icons-material/Extension';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import RoomPreferencesIcon from '@mui/icons-material/RoomPreferences';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import type { AlertColor } from '@mui/material';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import PageLoader from 'components/PageLoader';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from 'utils/axios';
import Config from 'utils/Config';
import { identifiers } from 'utils/constants/identifiers';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import { parentOrgInitData, User } from 'utils/redux/reducer/authentication-slice';
import { fetchFromStorage } from 'utils/storage';
import { downloadExportFile } from 'utils/validators/HelperFunctions';
import PropertyOnboardingRecordsDrawer from './PropertyOnboardingRecordsDrawer';
import PropertyOnboardingAddOnImportDrawer from './PropertyOnboardingAddOnImportDrawer';
import PropertyOnboardingImportDrawer from './PropertyOnboardingImportDrawer';

type OnboardingItemKey =
  | 'eventTypes'
  | 'venues'
  | 'packages'
  | 'addons'
  | 'seatingStyles'
  | 'venueConfigurations';

type OnboardingItemConfig = {
  key: OnboardingItemKey;
  label: string;
  icon: SvgIconComponent;
  createRoute: string;
  downloadTemplateApi: string | null;
  importApi: string | null;
  validateApi?: string;
  importInfoText?: string;
  previewEmptyText?: string;
  hideImportExport?: boolean;
  bulkCreate?: boolean;
};

type OnboardingStatusItem = {
  key: OnboardingItemKey;
  count: number;
  complete: boolean;
  weight: number;
};

type OnboardingStatusResponse = {
  propertyName: string;
  organizationId: number;
  items: OnboardingStatusItem[];
  progressPercent: number;
  venueConfigurationsEnabled: boolean;
};

const ONBOARDING_ITEMS: OnboardingItemConfig[] = [
  {
    key: 'eventTypes',
    label: strings.property_onboarding_event_types,
    icon: EventIcon,
    createRoute: routes.eventType,
    downloadTemplateApi: URLS.download_import_eventTypes_template,
    importApi: URLS.import_eventTypes,
    validateApi: URLS.import_eventTypes_validate,
    importInfoText: strings.property_onboarding_import_common_info,
    previewEmptyText: strings.property_onboarding_import_preview_empty_event_types,
  },
  {
    key: 'venues',
    label: strings.property_onboarding_venues,
    icon: LocationCityIcon,
    createRoute: routes.addVenue,
    downloadTemplateApi: URLS.download_import_venues_template,
    importApi: URLS.import_venues,
    validateApi: URLS.import_venues_validate,
    previewEmptyText: strings.property_onboarding_import_preview_empty_venues,
  },
  {
    key: 'packages',
    label: strings.property_onboarding_packages,
    icon: LocalOfferIcon,
    createRoute: routes.pricePackage,
    downloadTemplateApi: URLS.download_import_pricePackages_template,
    importApi: URLS.import_pricePackages,
    validateApi: URLS.import_pricePackages_validate,
    previewEmptyText: strings.property_onboarding_import_preview_empty_packages,
  },
  {
    key: 'addons',
    label: strings.property_onboarding_addons,
    icon: ExtensionIcon,
    createRoute: routes.addOns,
    downloadTemplateApi: URLS.download_import_addons_template,
    importApi: URLS.import_addOn,
    validateApi: URLS.import_addOn_validate,
    previewEmptyText: strings.property_onboarding_addon_preview_empty,
  },
  {
    key: 'seatingStyles',
    label: strings.property_onboarding_seating_styles,
    icon: EventSeatIcon,
    createRoute: routes.layout,
    downloadTemplateApi: URLS.download_import_layouts_template,
    importApi: URLS.import_layouts,
    validateApi: URLS.import_layouts_validate,
    importInfoText: strings.property_onboarding_import_common_info,
    previewEmptyText: strings.property_onboarding_import_preview_empty_layouts,
  },
  {
    key: 'venueConfigurations',
    label: strings.property_onboarding_venue_configurations,
    icon: RoomPreferencesIcon,
    createRoute: routes.bulkCreateVenueConfiguration,
    downloadTemplateApi: null,
    importApi: null,
    hideImportExport: true,
    bulkCreate: true,
  },
];

const EMPTY_COUNTS: Record<OnboardingItemKey, number> = {
  eventTypes: 0,
  venues: 0,
  packages: 0,
  addons: 0,
  seatingStyles: 0,
  venueConfigurations: 0,
};

const getProgressColor = (percent: number): 'error' | 'warning' | 'success' => {
  const rounded = Math.round(percent);
  if (rounded < 50) return 'error';
  if (rounded < 85) return 'warning';
  return 'success';
};

const PropertyOnboarding: FC = () => {
  const navigate = useCustomNavigate();
  const user = useSelector(User) as { role?: { value?: string } } | null;
  const { title } = useSelector(parentOrgInitData) as { title?: string };

  const isSuperAdmin = user?.role?.value === 'ROLE_BBR_ADMIN';

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<OnboardingItemKey | null>(null);
  const [status, setStatus] = useState<OnboardingStatusResponse | null>(null);
  const [recordsDrawerItem, setRecordsDrawerItem] = useState<OnboardingItemConfig | null>(null);
  const [addOnImportFile, setAddOnImportFile] = useState<File | null>(null);
  const [addOnImportOpen, setAddOnImportOpen] = useState(false);
  const [genericImportItem, setGenericImportItem] = useState<OnboardingItemConfig | null>(null);
  const [genericImportFile, setGenericImportFile] = useState<File | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const fileInputRefs = useRef<Partial<Record<OnboardingItemKey, HTMLInputElement | null>>>({});

  const token = fetchFromStorage(identifiers.access_token);

  const recordCounts = useMemo(() => {
    const counts = { ...EMPTY_COUNTS };
    status?.items.forEach(item => {
      if (item.key in counts) {
        counts[item.key as OnboardingItemKey] = item.count;
      }
    });
    return counts;
  }, [status]);

  const fetchOnboardingStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<OnboardingStatusResponse>(URLS.propertyOnboardingStatus);
      setStatus(data);
    } catch {
      setSnackbar({
        open: true,
        message: strings.property_onboarding_load_error,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOnboardingStatus();
    }
  }, [fetchOnboardingStatus, isSuperAdmin]);

  const onboardingPercent = status?.progressPercent ?? 0;
  const venueConfigPrerequisitesMet = status?.venueConfigurationsEnabled ?? false;
  const propertyName = status?.propertyName ?? title ?? '—';
  const progressColor = getProgressColor(onboardingPercent);

  const showComingSoon = () => {
    setSnackbar({
      open: true,
      message: strings.property_onboarding_coming_soon,
      severity: 'info',
    });
  };

  const handleDownloadTemplate = async (item: OnboardingItemConfig) => {
    if (!item.downloadTemplateApi) {
      showComingSoon();
      return;
    }
    try {
      setActionLoading(item.key);
      const response = await axios({
        url: `${Config.BaseURL + item.downloadTemplateApi}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      downloadExportFile(response, 'xlsx', `${item.key}-template`);
    } catch {
      setSnackbar({
        open: true,
        message: strings.property_onboarding_download_error,
        severity: 'error',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUploadTemplate = async (item: OnboardingItemConfig, file: File) => {
    if (!item.importApi) {
      showComingSoon();
      return;
    }
    try {
      setActionLoading(item.key);
      const body = new FormData();
      body.append('file', file);
      await axios({
        url: `${Config.BaseURL + item.importApi}`,
        method: 'POST',
        data: body,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      setSnackbar({
        open: true,
        message: strings.import_success,
        severity: 'success',
      });
      await fetchOnboardingStatus();
    } catch (error: any) {
      if (error?.response?.status === 406) {
        downloadExportFile(error.response.data, 'xlsx', 'errorFile');
        setSnackbar({
          open: true,
          message: strings.import_booking_error,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: strings.property_onboarding_upload_error,
          severity: 'error',
        });
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleFileChange = (item: OnboardingItemConfig, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (item.validateApi) {
      if (item.key === 'addons') {
        setAddOnImportFile(file);
        setAddOnImportOpen(true);
      } else {
        setGenericImportItem(item);
        setGenericImportFile(file);
      }
      return;
    }

    handleUploadTemplate(item, file);
  };

  const closeAddOnImportDrawer = () => {
    setAddOnImportOpen(false);
    setAddOnImportFile(null);
  };

  const closeGenericImportDrawer = () => {
    setGenericImportItem(null);
    setGenericImportFile(null);
  };

  const handleImportSuccess = async () => {
    setSnackbar({
      open: true,
      message: strings.import_success,
      severity: 'success',
    });
    await fetchOnboardingStatus();
  };

  const handleAddOnImportSuccess = handleImportSuccess;

  const isItemDisabled = (item: OnboardingItemConfig) =>
    item.key === 'venueConfigurations' && !venueConfigPrerequisitesMet;

  const getCreateTooltip = (item: OnboardingItemConfig) => {
    if (isItemDisabled(item)) {
      return strings.property_onboarding_venue_config_disabled;
    }
    return item.bulkCreate ? strings.property_onboarding_bulk_create : strings.property_onboarding_create;
  };

  const openRecordsDrawer = (item: OnboardingItemConfig) => {
    setRecordsDrawerItem(item);
  };

  const closeRecordsDrawer = () => {
    setRecordsDrawerItem(null);
  };

  if (!isSuperAdmin) {
    return <Navigate to={routes.dashboard} replace />;
  }

  return (
    <>
      {loading && <PageLoader />}
      <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {strings.property_onboarding_title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {propertyName}
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
            <CircularProgress
              variant="determinate"
              value={onboardingPercent}
              size={48}
              thickness={4}
              color={progressColor}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography variant="caption" fontWeight={700}>
                {onboardingPercent}%
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Paper variant="outlined" sx={{ px: 2, py: 1.25, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                {strings.property_onboarding_progress_label.replace('{percent}', String(onboardingPercent))}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={onboardingPercent}
                color={progressColor}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
          </Stack>
        </Paper>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, py: 1 }}>{strings.property_onboarding_item_column}</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1 }} align="center">
                  {strings.property_onboarding_records_column}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1 }} align="center">
                  {strings.property_onboarding_status_column}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1 }} align="right">
                  {strings.property_onboarding_actions_column}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ONBOARDING_ITEMS.map(item => {
                const count = recordCounts[item.key];
                const isComplete = count > 0;
                const isItemLoading = actionLoading === item.key;
                const disabled = isItemDisabled(item);

                return (
                  <TableRow
                    key={item.key}
                    hover={!disabled}
                    sx={disabled ? { opacity: 0.55, bgcolor: 'action.hover' } : undefined}>
                    <TableCell sx={{ py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: 0.75,
                            bgcolor: 'action.hover',
                            color: disabled ? 'text.disabled' : 'primary.main',
                            flexShrink: 0,
                          }}>
                          <item.icon sx={{ fontSize: 16 }} />
                        </Box>
                        <Box minWidth={0}>
                          <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
                          {disabled && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                              {strings.property_onboarding_venue_config_disabled}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {loading ? '—' : count}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      {loading ? (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      ) : isComplete ? (
                        <CheckCircleOutlineIcon color="success" fontSize="small" aria-label={strings.property_onboarding_complete} />
                      ) : (
                        <CancelOutlinedIcon color="error" fontSize="small" aria-label={strings.property_onboarding_incomplete} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {!item.hideImportExport && (
                          <>
                            <input
                              type="file"
                              hidden
                              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                              ref={el => {
                                fileInputRefs.current[item.key] = el;
                              }}
                              onChange={e => handleFileChange(item, e)}
                            />
                            <Tooltip title={strings.download_template_text}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={disabled || isItemLoading}
                                  onClick={() => handleDownloadTemplate(item)}
                                  aria-label={strings.download_template_text}>
                                  <DownloadRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={strings.property_onboarding_upload_template}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={disabled || isItemLoading}
                                  onClick={() => fileInputRefs.current[item.key]?.click()}
                                  aria-label={strings.property_onboarding_upload_template}>
                                  <FileUploadRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title={getCreateTooltip(item)}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={disabled || isItemLoading}
                              onClick={() => navigate(item.createRoute)}
                              aria-label={getCreateTooltip(item)}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={strings.property_onboarding_view_records}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={disabled || isItemLoading}
                              onClick={() => openRecordsDrawer(item)}
                              aria-label={strings.property_onboarding_view_records}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {strings.property_onboarding_footer_note}
        </Typography>
      </Container>

      <PropertyOnboardingRecordsDrawer
        open={Boolean(recordsDrawerItem)}
        itemKey={recordsDrawerItem?.key ?? null}
        title={
          recordsDrawerItem
            ? strings.property_onboarding_records_drawer_title.replace('{item}', recordsDrawerItem.label)
            : ''
        }
        onClose={closeRecordsDrawer}
      />

      <PropertyOnboardingAddOnImportDrawer
        open={addOnImportOpen}
        file={addOnImportFile}
        onClose={closeAddOnImportDrawer}
        onImportSuccess={handleAddOnImportSuccess}
      />

      <PropertyOnboardingImportDrawer
        open={Boolean(genericImportItem)}
        file={genericImportFile}
        title={
          genericImportItem
            ? strings.property_onboarding_import_title.replace('{item}', genericImportItem.label)
            : ''
        }
        validateUrl={genericImportItem?.validateApi ?? ''}
        importUrl={genericImportItem?.importApi ?? ''}
        previewEmptyText={genericImportItem?.previewEmptyText ?? ''}
        infoText={genericImportItem?.importInfoText}
        onClose={closeGenericImportDrawer}
        onImportSuccess={handleImportSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PropertyOnboarding;
